// src/features/testimonials/TestimonialsAdmin.jsx
import React, { useEffect, useState } from "react";
import "./testimonial-admin.css";
import TestimonialForm from "./TestimonialForm";
import TestimonialCard from "./TestimonialCard";
import { apiGet, apiDel } from "../../admin/api";

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all"); // "all" | "published" (public default) | "draft"
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const limit = 12;

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set("page", String(page));
      qs.set("limit", String(limit));
      if (q.trim()) qs.set("q", q.trim());
      // hit backend with ?status=all to see all; otherwise default behaviour shows published only
      if (status === "all") qs.set("status", "all");
      if (status === "draft") qs.set("status", "draft"); // controller accepts 'all' or filters we apply after load

      const data = await apiGet(`/api/testimonials?${qs.toString()}`);

      // If status === 'draft' and backend default is published-only,
      // we filter client-side to show just drafts (in case controller isn't filtering for draft directly).
      const list = Array.isArray(data.items) ? data.items : [];
      const filtered =
        status === "draft"
          ? list.filter((it) => it.status === "draft")
          : status === "published"
          ? list.filter((it) => it.status === "published")
          : list;

      setItems(filtered);
    } catch (e) {
      alert(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await apiDel(`/api/testimonials/${id}`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };
  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  const showPager = page > 1 || (items?.length || 0) === limit;

  return (
    <div className="t-admin">
      <div className="toolbar">
        <h2 className="toolbar__title">Testimonials</h2>
        <div className="toolbar__actions">
          <form onSubmit={onSearch} className="searchbar">
            <input
              className="searchbar__input"
              placeholder="Search by name, title, message…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select
              className="searchbar__select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
            <button className="btn" type="submit">
              <span className="i i-search" /> Search
            </button>
          </form>

          <button className="btn btn--cta" onClick={openCreate} title="Add Testimonial">
            <span className="i i-plus" aria-hidden="true" />
            Add Testimonial
          </button>
        </div>
      </div>

      {showForm ? (
        <div className="panel">
          <div className="panel__head">
            <h3 className="panel__title">{editing ? "Edit Testimonial" : "Add Testimonial"}</h3>
          </div>
          <TestimonialForm
            initial={editing}
            onSaved={() => { closeForm(); load(); }}
            onCancel={closeForm}
          />
        </div>
      ) : (
        <>
          {loading ? (
            <div className="empty">Loading…</div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty__icon">💬</div>
              <div className="empty__title">No testimonials</div>
              <div className="empty__desc">Click the button to add your first testimonial.</div>
              <button className="btn btn--cta" onClick={openCreate}>
                <span className="i i-plus" /> Add testimonial
              </button>
            </div>
          ) : (
            <div className="t-grid">
              {items.map((it) => (
                <TestimonialCard
                  key={it.id}
                  item={it}
                  onEdit={(m) => { setEditing(m); setShowForm(true); }}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}

          {showPager && (
            <div className="pager">
              <button
                className="pill"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <span className="i i-left" aria-hidden="true" />
                Prev
              </button>

              <span className="badge">Page {page}</span>

              <button className="pill" onClick={() => setPage((p) => p + 1)}>
                Next
                <span className="i i-right" aria-hidden="true" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
